'use client';

/**
 * usePlanLensesReading / usePlanRoomsReading — the model's reading, fetched
 * on its own. The lens and rooms facts are computed without any model call and
 * paint straight away; the reading is the one slow part, so it has its own
 * strategy and its own loading state and never holds the cards up.
 *
 * @see klyde_edge/klyde_intelligence/projects/handler.py — plan_lenses_reading, plan_rooms_reading
 *
 * @component_role: Hook
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useStrategyData } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCapabilityStore } from '@/lib/stores/capability-store';

// A reading is an LLM call: keep it for an hour rather than the 15-minute default.
const READING_STALE_MS = 60 * 60 * 1000;

function useReading(action: 'plan_lenses_reading' | 'plan_rooms_reading') {
  const { mcpId } = useViewportSelection();
  const strategy = useCapabilityStore((s) => s.strategyName('cost_plans', action));
  const { data, isLoading } = useStrategyData<{ narrative: string }>(
    strategy, { semantic_id: mcpId ?? '' }, !!mcpId && !!strategy, READING_STALE_MS,
  );
  const queryKey = strategy ? ['strategy-data', strategy, { semantic_id: mcpId ?? '' }] : null;
  return { mcpId, strategy, narrative: data?.data?.narrative, isLoading, queryKey };
}

export const usePlanLensesReading = () => useReading('plan_lenses_reading');
export const usePlanRoomsReading = () => useReading('plan_rooms_reading');
